<template>
  <section v-once class="section row align-center">
    <header class="columns small-12 large-8">
      <h1 class="text-primary">Terms &amp; Conditions</h1>
    </header>

    <div class="columns small-12 large-8">
      <h2>Rules for Use of this Website</h2>
      <p>
        Before you go further please take a moment to read our Terms and Conditions and
        <strong>
          <nuxt-link to="/about/privacy-policy/" title="View Teach Starter Privacy Policy">
            Privacy Policy
          </nuxt-link>
        </strong>
        – this is important for you and us. By utilizing the remainder of this website and related
        apps, you acknowledge that you have reviewed the Terms and Conditions and Privacy Policy and
        agree to them.
      </p>
      <p>
        YOU ACKNOWLEDGE AND AGREE THAT, BY ACCESSING OR USING THE SITE, APPLICATION OR SERVICES OR
        BY DOWNLOADING OR POSTING ANY CONTENT FROM OR ON THE SITE, VIA THE APPLICATION OR THROUGH
        THE SERVICES, YOU ARE INDICATING THAT YOU HAVE READ, AND THAT YOU UNDERSTAND AND AGREE TO BE
        BOUND BY THESE TERMS, WHETHER OR NOT YOU HAVE REGISTERED WITH THE SITE AND APPLICATION. IF
        YOU DO NOT AGREE TO THESE TERMS, THEN YOU HAVE NO RIGHT TO ACCESS OR USE THE SITE,
        APPLICATION, SERVICES OR COLLECTIVE CONTENT. If you accept or agree to these Terms on behalf
        of a company or other legal entity, you represent and warrant that you have the authority to
        bind that company or other legal entity to these Terms and, in such event, “User”, “you” and
        “your” will refer and apply to that company or other legal entity. You further represent
        that you are over 18 years of age and that you are located in a jurisdiction wherein you can
        enter into legally binding agreements and internet based terms and conditions.
      </p>
      <h2>Terms of Use</h2>
      <p>
        Teach Starter Pty Ltd ABN 69 634 768 817 (
        <strong>Teach Starter</strong>
        ) owns and operates this website and all related subdomains located at
        <a href="https://www.teachstarter.com" title="Teach Starter website">
          www.teachstarter.com
        </a>
        <strong>(Site)</strong>
        as well as related iOS and Android Apps and other applications. Access to and use of this
        Site and the products and services available through this Site (collectively
        <strong>Resources</strong>
        ) is subject to the following terms, conditions and notices (
        <strong>Terms of Use</strong>
        ).
      </p>
      <p>
        These Terms of Use apply to any person (
        <strong>Users</strong>
        ) who obtains access to the Site, whether or not they take up a Subscription.
      </p>
      <p>
        If Users use the Resources, they are deemed to have agreed to all of the Terms of Use, as
        may be updated by Teach Starter from time to time. Users should check this page regularly
        for notice of any changes which may have been made to the Terms of Use, which will apply
        from the date of posting.
      </p>
      <p>
        By viewing the Site, Users agree to do so in accordance with the Terms of Use, including all
        relevant acknowledgements and waivers.
      </p>
      <p>
        By using the Resources, a User enters into a binding contract between Teach Starter and the
        User on the Terms of Use and any additional terms which may appear on the Site in relation
        to a particular Resource, such as price and availability.
      </p>
      <p>
        Users acknowledge and agree that they have read and understood these Terms of Use and agree
        to be bound by them.
      </p>
      <p>
        Teach Starter reserves the right to amend these Terms of Use from time to time and any
        amendments will be effective immediately upon notification on this Site. Those amendments
        will not affect any existing contractual relationship but will govern any future use of the
        Site following such notification after which Users will be bound by the amended Terms of
        Use.
      </p>
      <p>Users who do not agree to these Terms of Use must not continue to use the Site.</p>
      <p>
        Access to the Site is permitted on a temporary basis and Teach Starter reserves the right to
        withdraw or amend the Resources without notice. Teach Starter will not be liable if for any
        reason the Site is unavailable at any time or for any period and Teach Starter may from time
        to time restrict access to some parts or all of the Site. See clause 6.
      </p>
      <p>
        The Teach Starter Privacy Policy which is available on this Site forms part of these Terms
        of Use and Users warrant that all data provided by them is accurate.
      </p>
      <h3>1. Definitions and interpretation</h3>
      <h4>1.1 Defined terms</h4>
      <p>
        <strong>Application</strong>
        means any application software that is available on the Site or a third party website or
        online store for the User to access, download (if applicable) and use in relation to Teach
        Starter resources is included within the definition of the Site.
      </p>
      <p>
        <strong>Claim</strong>
        includes any claim, action, proceeding, loss (including consequential loss), damages, costs,
        expenses, requisition, objection, alleged right of indemnity incurred or suffered by, or
        brought or made or recovered against a person, no matter how arising (whether or not
        presently ascertained) in the immediate future or contingent.
      </p>
      <p>
        <strong>Commencement Date</strong>
        means:
      </p>
      <ol style="list-style-type: lower-alpha">
        <li>
          for a Free Subscription: the date the User checks the “I accept” box when creating a User
          account.
        </li>
        <li>for a Paid Subscription: the date the User pays the Subscription Fee.</li>
      </ol>
      <p>
        <strong>Content</strong>
        means all textual, visual, aural or other sensory content including text, images, sounds,
        videos, animations, graphics, data, products, materials, services, links, technology, code,
        language, functions, software and applications or any other content made available on or
        through the Site (whether known or invented in the future).but does not include Third part
        Products.
      </p>
      <p>
        <strong>Free Subscription</strong>
        means all of the rights (including a licence) granted to Users to have access to and use the
        Free Materials in accordance with these Terms of Use and pursuant to clause 3.
      </p>
      <p>
        <strong>Free Materials</strong>
        means the free Resources and Application that are available for Users to download and use
        from the Site for free.
      </p>
      <p>
        <strong>Initial Period</strong>
        means the one (1) year period commencing on the date the User pays the Subscription Fee.
      </p>
      <p>
        <strong>Insolvency Event</strong>
        means circumstances in which Teach Starter or a User take any corporate action or any steps
        are taken or legal proceedings are started for:
      </p>
      <ol style="list-style-type: lower-alpha">
        <li>its winding-up, dissolution, or liquidation;</li>
        <li>
          the appointment of a controller, receiver, administrator, official manager, trustee or
          similar officer of it; or
        </li>
        <li>
          seeking or being granted protection from its creditors, under any applicable legislation.
        </li>
      </ol>
      <p>
        <strong>Intellectual Property</strong>
        means all industrial and intellectual property rights including, without limitation,
        patents, copyrights, right to extract information from databases, design rights, trade
        secrets, rights of confidence, and all forms of protection of a similar nature or having
        similar effect to any of them which may subsist anywhere in the world (whether or not any of
        them are registered and including applications and the right to make applications for
        registration of any of them).
      </p>
      <p>
        <strong>Materials</strong>
        means one or both of the Free Materials and Paid Materials as the context requires.
      </p>
      <p>
        <strong>Paid Subscription</strong>
        means all of the rights (including a licence) granted to Users to have access to and use the
        Paid Materials in accordance with these Terms of Use and pursuant to clause 3.
      </p>
      <p>
        <strong>Paid Materials</strong>
        means the Resources and the Application that the User is granted access to pursuant to their
        Paid Subscription.
      </p>
      <p>
        <strong>Personnel</strong>
        , in relation to a school, includes all full time, part time and casual employees,
        contractors, officers and members of any controlling body (for instance, a School Council or
        a Board of Trustees), agents or other persons with apparent or ostensible authority to act
        on behalf of the school.
      </p>
      <p>
        <strong>Plan</strong>
        means a Subscription package by which Users agree to pay to Teach Starter a Subscription Fee
        for the Resources available under a chosen plan including all of the terms, conditions,
        rights and obligations attaching to that Plan.
      </p>
      <p>
        <strong>Renewal Period</strong>
        means the one (1) year term commencing on the expiration of the Initial Period and any
        subsequent renewal period.
      </p>
      <p>
        <strong>Subscription</strong>
        means a commercial arrangement between a User and Teach Starter involving the supply of
        Products under licence and includes a Free Subscription, a Paid Subscription or any other
        subscription or licence expressed or implied at law, as the context requires.
      </p>
      <p>
        <strong>Subscription Fee</strong>
        means the yearly fee payable by the User in accordance with the Teach Starter Plan the User
        has chosen.
      </p>
      <p>
        <strong>Term</strong>
        means the Initial Period and each subsequent Renewal Period as applicable.
      </p>
      <p>
        <strong>Terms of Use</strong>
        includes terms or conditions which relate to a relevant Resource and which may appear on the
        Site and will also constitute the terms of use of any Application.
      </p>
      <p>
        <strong>Third Party Product</strong>
        means any product or service advertised or made available for sale on the Site by a party
        other than Teach Starter.
      </p>
      <h4>1.2 Interpretation</h4>
      <p>Reference to:</p>
      <ol style="list-style-type: lower-alpha">
        <li>the singular includes the plural and the plural includes the singular;</li>
        <li>a person includes a body corporate;</li>
        <li>
          to an amount of money, to $, $A or dollar is a reference to the currency of Australia;
        </li>
        <li>
          a statute, regulation or provision of a statute or regulation (a Statutory Provision)
          includes:
          <ol style="list-style-type: lower-roman">
            <li>that Statutory Provision as amended or re-enacted from time to time; and</li>
            <li>
              a statute, regulation or provision enacted in replacement of that Statutory Provision;
            </li>
          </ol>
        </li>

        <li>including and similar expressions are not words of limitation;</li>
        <li>
          where a word or expression is given a particular meaning, other parts of speech and
          grammatical forms of that word or expression have a corresponding meaning;
        </li>
        <li>
          headings and the table of contents are for convenience only and do not form part of these
          Terms of Use or affect their interpretation.
        </li>
      </ol>
      <h3>2. Access to Site</h3>
      <h4>2.1</h4>
      <p>
        If a person wishes to obtain access to this Site, they may do so only in the following
        capacity:
      </p>
      <ol style="list-style-type: lower-alpha">
        <li>a casual User;</li>
        <li>under a Free Subscription;</li>
        <li>under a Paid Subscription;</li>
        <li>
          in accordance with any other terms and conditions which relate to a relevant Resource to
          which a User intends to Subscribe or has Subscribed and which appear on the Site in
          relation to those particular Resources;
        </li>
        <li>schools, in accordance with a Relevant Subscription;</li>
        <li>
          students, in accordance with the terms and conditions relating to any Resource to which
          they are authorised to have access;
        </li>
        <li>other persons in accordance with the terms of their Subscription;</li>
        <li>a buyer or licensee of a Third Party Product.</li>
      </ol>

      <h3>3. Subscriptions</h3>
      <h4>3.1 Free Subscription</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          The Free Subscription commences on the Commencement Date and continues until the User’s
          account is terminated.
        </li>
        <li>
          Teach Starter grants Users a non-exclusive, non-transferable, fee-free, royalty-free
          limited licence to use the Free Materials in accordance with these Terms of Use until the
          User’s account is terminated or has expired under these Terms of Use.
        </li>
        <li>
          The User agrees to use and access the Free Materials on the terms of the Subscription and
          for the purpose for which the Free Materials are provided.
        </li>
      </ol>

      <h4>3.2 Paid Subscription</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          The Paid Subscription commences on the Commencement Date and will continue for the Term.
        </li>
        <li>
          Teach Starter grants Users a non-exclusive, non-transferable, limited licence to use the
          Paid Materials for the Term in exchange for the Subscription Fee.
        </li>
        <li>
          Users agree to use and access the Paid Materials on the terms of the Paid Subscription and
          for the purpose for which the Paid Materials are provided.
        </li>
        <li>
          The Paid Subscription will automatically renew for the Renewal Period, unless the User
          terminates the Paid Subscription by cancelling their recurring subscription in accordance
          with the procedures contained in Teach Starter’s Help Desk.
        </li>
        <li>
          If Teach Starter increases the Subscription Fee during the Term, Users agree to pay the
          increased Subscription Fee, when the Paid Subscription renews for the Renewal Period.
        </li>
        <li>
          If Users purchase a discounted Paid Subscription as a part of a promotional campaign,
          Users agree to pay the full Subscription Fee without discounts when the Paid Subscription
          renews for the Renewal Period.
        </li>
        <li>Users agree to pay the Subscription Fee in the manner stipulated by Teach Starter.</li>
        <li>
          Users are responsible for payment of all applicable taxes in relation to the Subscription,
          unless Teach Starter advises otherwise.
        </li>
      </ol>

      <h4>3.3 Unauthorised access and use</h4>
      <p>
        Subject to the specific terms of any Subscription, Users must not under any circumstances:
      </p>

      <ol style="list-style-type: lower-alpha">
        <li>provide Materials to any teachers, parents, or other unauthorised users;</li>
        <li>resell any Materials or edit and resell any Materials;</li>
        <li>use any Materials for profit or a personal benefit; or</li>
        <li>allow any third parties to use their Subscription.</li>
      </ol>

      <h4>3.4 Ownership</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter owns or is the licensee of all Intellectual Property in the Content and
          Site.
        </li>
        <li>
          Users acknowledge that they do not acquire any Intellectual Property, either express or
          implied, in the Content and Site other than a Subscription licence under these Terms of
          Use.
        </li>
        <li>
          Third Party Products are not owned by Teach Starter and may be purchased or licensed
          through the Site in the manner displayed, either from the third party or from Teach
          Starter as agent.
        </li>
      </ol>

      <h4>3.5 No obligation to support</h4>
      <p>
        Subject to clause 11 and any consumer legislation to the contrary, provided the Materials
        and other Resources function and are fit for purpose, Teach Starter has no obligation to
        provide support, maintenance, upgrades, modifications or new releases of the Materials and
        other Resources.
      </p>

      <h4>3.6 User Generated Content</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Users may, depending on the terms of their Paid Subscription, generate customised content
          in relation to a Teach Starter Resource or new Resources for any authorised use
          (Customised Resources).
        </li>
        <li>
          Users grant Teach Starter an irrevocable, worldwide , royalty-free, fee-free, perpetual
          licence with the right to sublicense to use, exploit for monetary gain, which monetary
          gain shall belong exclusively to Teach Starter, reproduce and make available all the
          User’s customisations of any Teach Starter Resources.
        </li>
        <li>
          Users may display their Customised Resources on the Site for other authorised persons to
          access, view and use.
        </li>
        <li>
          Users must not upload any Customised Resources which contain identifying details or
          information relating to students, including but not limited to students’ names,
          photographs of the students, class names, and any other identifying information.
        </li>
        <li>
          By allowing other persons to access, view and use the Users’ Customised Resources through
          the Site, each User represents and warrants that:

          <ol style="list-style-type: lower-roman">
            <li>
              they are responsible for all of the Content and customisation of the Customised
              Resources and for access to the Site by other persons through them;
            </li>
            <li>
              the Customised Resources do not contain any identifying information of the any
              student;
            </li>
            <li>their Customised Resources are appropriate for other persons to use;</li>
            <li>
              their Customised Resources do not contain any content that could be considered
              illegal, defamatory, obscene, or could be reasonably be considered to be offensive to
              any group or individual or in any way contrary to the public morality of any place or
              country;. untrue , express or implied, or in contravention of any applicable law,
              including without limitation laws and regulations relating to false advertising or
              political in nature ; or are unsuitable for students
            </li>
            <li>
              all materials used are the property of the User and the use of them and the
              customisation will not be in actual or potential breach of the Intellectual Property
              rights of any other person, nor such person’s right of publicity or right of privacy.
            </li>
          </ol>
        </li>
        <li>
          Users acknowledge that Teach Starter may remove any of the User’s Customised Resources if
          Teach Starter, in its sole discretion, considers that the Customised Resources breach any
          of the representations and warranties contained in clause 3.6(d)or are otherwise
          undesirable or may tend to damage the goodwill and reputation of Teach Starter.
        </li>
        <li>
          Users may not sell or share their Customised Resources on third-party websites or stores
          unless they have received prior written approval from Teach Starter, which approval Teach
          Starter may withhold in its sole discretion.
        </li>
        <li>
          Users may not claim attribution or authorship to any Customised Resources ion third-party
          website, advertising, promotional material or stores unless they have received prior
          written approval from Teach Starter which, approval Teach Starter may withhold in its sole
          discretion.

          <ol style="list-style-type: lower-roman">
            <li>
              A User who posts any material, comment, opinion or representation on the Site must
              ensure that in doing so, they comply with clause 3.6(c) and agree that the terms of
              clause 3.6(e) will apply.
            </li>
          </ol>
        </li>
      </ol>
      <h4>3.7 Schools and teachers</h4>
      <p>If the User is a school or teacher:</p>
      <ol style="list-style-type: lower-alpha">
        <li>
          they are responsible for ensuring that their Personnel or students access the Materials in
          accordance with these Terms of Use and their relevant Subscription; and
        </li>
        <li>
          their Subscription may be terminated if:
          <ol style="list-style-type: lower-roman">
            <li>
              any of their Personnel or students breach the terms and conditions of these Terms of
              Use or their relevant Subscription; or
            </li>
            <li>
              the number of Personnel or students accessing the Materials exceeds the limit
              contained in their relevant Subscription.
            </li>
          </ol>
        </li>
      </ol>

      <h4>3.8 Account and password</h4>
      <ol style="list-style-type: lower-alpha">
        <li>Users must create a Site account to access the Materials.</li>
        <li>Users must keep all usernames and passwords to their account strictly confidential.</li>
        <li>
          Users are expressly prohibited from sharing their account details with third parties,
          however they may access the Site from any location.
        </li>
        <li>Such passwords shall be unique and not used by the User for any other purpose.</li>
        <li>
          Teach Starter shall not be liable in any way whatsoever for the accidental disclosure by
          Teach Starter or the unauthorized use by a third party of the User’s password.
        </li>
      </ol>

      <h4>3.9 Termination by Teach Starter</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter reserves the right to limit, cancel, suspend or terminate a Subscription
          without notice in its absolute discretion if:
          <ol style="list-style-type: lower-roman">
            <li>
              it no longer has the right to provide relevant Content and Materials to the User for
              any reason whatsoever;
            </li>
            <li>
              the User commits a breach of these Terms of Use or the Subscription, including but not
              limited to providing the Materials to unauthorised users;
            </li>
            <li>use of the Content may breach a third party’s Intellectual Property rights;</li>
            <li>
              activities being conducted by the User contravene any applicable law or regulation;
            </li>
            <li>
              the User does not pay the Subscription Fee for the Renewal Period prior to the Renewal
              Period commencing;
            </li>
            <li>the User is a body corporate and suffers an Insolvency Event;</li>
            <li>the User is an individual and becomes bankrupt.</li>
            <li>
              the User breaches any of the representations and warranties set forth clause 3.6 or it
              learns that any such representations and warranties may be untrue in whole or in part.
            </li>
          </ol>
        </li>

        <li>
          If Teach Starter terminates the Subscription pursuant to this clause 3.9:
          <ol style="list-style-type: lower-roman">
            <li>the User will cease to have access to the Materials;</li>
            <li>
              the User will not be entitled to a refund of any Subscription Fees paid for the
              Subscription; and
            </li>
            <li>
              Teach Starter is not liable for Claim of any kind for terminating the User’s access to
              the Content and Site.
            </li>
          </ol>
        </li>
      </ol>

      <h4>3.10 Termination by the User for breach</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          A User can terminate a Subscription without notice if Teach Starter commits a breach of
          any of the material terms and conditions of the Subscription.
        </li>
        <li>
          Teach Starter will refund the User the Subscription Fee for the Initial Period or the
          Renewal Period as applicable on a pro-rata basis if the Subscription is terminated
          pursuant to clause 3.10(a).
        </li>
      </ol>

      <h4>3.11 Actions upon termination</h4>
      <p>Upon termination of a Subscription:</p>
      <ol style="list-style-type: lower-alpha">
        <li>the User will cease to have access to the Content;</li>
        <li>the User must delete any downloaded or saved Resources;</li>
        <li>
          the User must cease using any Resources, except for Resources that have been printed prior
          to the Subscription being terminated;
        </li>
        <li>
          the User must cease to use and uninstall the Application from all devices the User has
          installed the Application on;
        </li>
        <li>at the option of Teach Starter, the User’s account will be suspended or closed;</li>
        <li>
          any Terms of Use which are capable of surviving termination of a Subscription will do so
          and will continue to be enforceable between the parties.
        </li>
      </ol>

      <h4>3.12 Third Party Products</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter may make available Third Party Products on the Site as agent for the owner;
        </li>
        <li>
          If a User wishes to purchase and download a Third Party Product, it must be done in
          accordance with any terms displayed by the owner but will be processed through the Site;
        </li>
        <li>
          Except where expressed to the contrary, these Terms of Use will not apply to Third Party
          Products and Users must ensure that the terms and conditions of use of the products are
          satisfactory and agreed to with the owner;
        </li>
        <li>
          Except to the extent to which the Australian Consumer Law applies, Teach Starter disclaims
          any responsibility or liability for the suitability, content standard and operation of
          Third Party Products and gives no warranties or guarantees for such matters and User
          acknowledges that the purchase and use of such Third Party Products is solely at its own
          risk
        </li>
        <li>
          The User acknowledges that Teach Starter may receive a commercial consideration from the
          sale of such products.
        </li>
        <li>
          The user acknowledges the images and trademarks on teach starter’s website are subject,
          respectively, to protection under coyright laws. and trademark laws. use of said images
          and/or trademarks on teach starter’s website does not mean that the images and/or
          trademarks are in the public domain or otherwise available for use without prior written
          consent from the owner’s thereof.
        </li>
      </ol>
      <h3>4. Permitted use of Content</h3>
      <h4>4.1</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Unless otherwise indicated, Teach Starter reserves all copyright in the Content and design
          of the Site and is the owner or licensee of all Intellectual Property rights.
        </li>
        <li>
          Users may download, print or copy Content for their own use, pursuant to their
          Subscription. Unless authorised and provided with a mechanism to do so by Teach Starter,
          Users must not sell, lease, deal with the Content and other Resources or permit or cause
          others to do so other than in accordance with the terms of their Subscription.
        </li>
        <li>
          Users must not use, reproduce, communicate, publish, distribute or otherwise deal with any
          of the Content on the Site, unless authorised under their Subscription, but not including
          fair dealing for the purposes of the Copyright Act 1968 (Cth) (Act). In particular, Users
          must not reproduce or use any of the Content for commercial benefit.
        </li>
        <li>
          Other than for the purposes of and subject to the conditions prescribed under the Act or
          as otherwise provided for in the Subscription and these Terms of Use, no part of the
          Content may in any form or by any means (including framing, screen scraping, electronic,
          mechanical, microcopying, photocopying or recording) be reproduced, adapted, stored in a
          retrieval system or transmitted without prior written permission.
        </li>
      </ol>

      <h3>5. Prohibitions on use</h3>
      <h4>5.1</h4>
      <p>
        The Site and Content must not be used in any manner that infringes Teach Starter’s rights or
        the rights of a third party, or otherwise misuses or damages the Site or Content. Users
        agree that they must not in relation to their use of the Site and the Content:
      </p>
      <ol style="list-style-type: lower-alpha">
        <li>
          data mine or conduct automated searches on the Site or the Contents whether through the
          use of additional software or otherwise;
        </li>
        <li>frame or mirror the Site;</li>
        <li>
          tamper with, hinder the operation of, or make unauthorised modifications to the Site or
          Content;
        </li>
        <li>
          transmit or distribute any virus, trojan, worm, disabling feature, malware or other
          material which is malicious, technologically harmful or in breach of confidence to or via
          the Site;
        </li>
        <li>
          commit, attempt, aid or encourage a criminal offence including but not limited to the
          breach of any laws relating to offences against or involving children and sexual offences;
        </li>
        <li>infringe upon the proprietary or personal rights of a third party;</li>
        <li>
          abuse, defame, harass, stalk, threaten, intimidate, discriminate or otherwise violate the
          legal rights of any person;
        </li>
        <li>
          advertise or offer to sell any goods or services, or conduct or forward surveys, contests,
          or chain letters from the Site or the domain;
        </li>
        <li>
          modify or delete any author attributions, legal notices or proprietary designations or
          labels associated with the Content;
        </li>
        <li>
          use the Site to send commercial, unsolicited or bulk electronic messages to anyone or in
          any other way which would constitute an infringement of the Spam Act 2003 (Cth) or any
          other similar or related State or Commonwealth legislation; or violate the laws and
          regulations of any country, including without limitation the Lanham Act of the United
          States, the Federal Trade Commission Act, and any and all laws and regulations relating to
          rights of privacy as set forth in Teach Starters Privacy terms and conditions as well as
          laws and regulations relating to rights of publicity.
        </li>
        <li>
          use the Content or post any material, comment, opinion or representation for any purpose
          or in any manner not contemplated in these Terms of Use or in a manner which could damage
          the reputation of Teach Starter or the goodwill or other rights enjoyed by Teach Starter;
          or
        </li>
        <li>
          transfer, sublicense, rent, lease or lend the Materials to a third party or use the
          Materials outside of the classroom or for commercial sharing.
        </li>
        <li>
          expressly represent or warrant that User is affiliated in any way with Teach Starter other
          than being a User
        </li>
      </ol>

      <h3>6. Provision of service</h3>
      <h4>6.1</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter may without notice suspend access to the Site or disconnect or deny Users
          access to any part of the Site during any technical failure or maintenance period.
        </li>
        <li>
          Teach Starter may also choose at its sole discretion to block or deny any particular User
          with access to any of the Content subject to the terms of any Subscription.
        </li>
        <li>
          Teach Starter may make improvements and or changes to the Content and Site at any time
          without notice. Teach Starter does not warrant that the information architecture or
          navigation will not change at any time.
        </li>
      </ol>

      <h3>7. Consent to the provision of information to third parties</h3>
      <h4>7.1</h4>
      <p>
        Subject to and at all times in accordance with the Privacy Policy, which Policy is
        integrated herein by reference:
      </p>
      <ol style="list-style-type: lower-alpha">
        <li>
          Users acknowledge that their details may be provided to third parties in order for Users
          to access the Content and the Site.
        </li>
        <li>
          Users acknowledge that information about them may be sent between countries to other
          entities that Teach Starter has commercial contracts with. The provision of such
          information will be under an obligation of confidence.
        </li>
        <li>
          Users acknowledge that their credit card details or PayPal billing agreement will be
          securely stored by Recurly and Stripe, in order for Users to access the Content and the
          Site and for Users’ subscriptions to be renewed.
        </li>
        <li>
          Users consent to the use of browser cookies in connection with the Site provided such use
          does not conflict with its Privacy Policy.
        </li>
      </ol>

      <h3>8. Links to third party sites</h3>
      <h4>8.1</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter does not represent that the content on other websites to which the Site
          contains links to does not inadvertently infringe the Intellectual Property rights of any
          person anywhere in the world.
        </li>
        <li>
          By inadvertently linking to infringing third party content, Teach Starter is not
          authorising infringement of any intellectual property rights contained on those websites.
          Please contact Teach Starter at designatedagent@teachstarter.com to notify Teach Starter
          of any links which are alleged to be infringing.
        </li>
      </ol>

      <h3>9. Take down procedure</h3>
      <h4>9.1</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Teach Starter takes all reasonable steps to be aware of Content published by Users on the
          Site. Teach Starter is committed to removing all Content as soon as practical after Teach
          Starter is notified and agrees that the Content may be a breach of copyright or other
          Intellectual Property rights, is defamatory or otherwise claimed to be illegal.
        </li>
        <li>
          Notice for Claims of Intellectual Property Violations and Copyright Infringement Pursuant
          to Section 512(c) of Title 17 of the United States Code. Teach Starter responds to notices
          of alleged copyright infringement under the United States Digital Millennium Copyright Act
          and work to ensure that listed items and content on its site or do not infringe upon the
          copyright, trademark, or other intellectual property rights of third parties. If User
          believes that your intellectual property rights have been infringed, please notify Teach
          Starter pursuant to the take down provisions below.
        </li>
        <li>
          Any person alleging any such breach or activity ( Complainant) must notify Teach Starter
          of such claim by sending a notification to designatedagent@teachstarter.com which
          includes:

          <ol style="list-style-type: lower-roman">
            <li>
              information about the complaining party including the capacity in which the
              Complainant makes the complaint e.g. as copyright owner or person otherwise affected
              by the Content, address, telephone number (land line and mobile) and email address;
            </li>
            <li>an electronic or physical signature of the Complainant;</li>
            <li>
              identification of the material that is claimed to be infringing or to be subject to
              infringing activity and that is sought to be removed and:

              <ol style="list-style-type: upper-alpha">
                <li>
                  information reasonably sufficient to permit Teach Starter to locate the material;
                </li>
                <li>
                  full particulars of why the material is alleged to breach copyright or other
                  Intellectual Property rights, is defamatory or is otherwise illegal;
                </li>
                <li>
                  any other facts which Teach Starter would reasonably require to form an opinion as
                  to the truth or otherwise of the Complainant’s allegations;
                </li>
              </ol>
            </li>
            <li>
              a statement that the Complainant has a good faith belief that use of the material in
              the manner complained of is not authorised by the copyright owner, agent, or the law;
              and
            </li>
            <li>
              a statement that the information in the notification and the facts contained in it
              upon which the Complainant relies are accurate, complete and, where the Complainant is
              the agent of a third party, that the Complainant is duly authorised to make the
              complaint.
            </li>
          </ol>
        </li>

        <li>
          Users and Complainants acknowledge and agree that if Teach Starter receives a notification
          under this clause 9, it may immediately remove the material from the Site, take no action
          or act as it may decide in its absolute discretion, without further notice and without any
          obligation to offer or pay compensation to any party.
        </li>
        <li>
          To notify Teach Starter of material that may require removal from the Site other than for
          the reasons set out in clause 9.1(a) please email:
          <a href="mailto:designatedagent@teachstarter.com" title="Email Designated Agent">
            designatedagent@teachstarter.com
          </a>
          .
        </li>
      </ol>

      <h3>10. No duty of care</h3>
      <h4>10.1</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Users access the Site at their own risk and are responsible for compliance with the laws
          of their jurisdiction in addition to those contained in these Terms of Use.
        </li>
        <li>
          Teach Starter makes the Site available for the Users to use, however Teach Starter does
          not assume a duty of care to the Users.
        </li>
        <li>
          Users must make their own investigations to ensure the Content is accurate and suitable
          for purpose before they rely upon it.
        </li>
      </ol>

      <h3>11. Warranties and Liability</h3>
      <h4>11.1 Warranties</h4>
      <p>
        Users expressly acknowledge and agree that, to the maximum extent permitted by law and
        subject to clause 11.3:
      </p>

      <ol style="list-style-type: lower-alpha">
        <li>their use of the Site and Content is at their sole risk.</li>
        <li>The Site and Content are provided on an “as is” and “as available” basis.</li>
        <li>
          Teach Starter makes no warranty that:

          <ol style="list-style-type: lower-roman">
            <li>the Content and Site will meet Users’ requirements;</li>
            <li>
              (ii) the Content and the Site will be uninterrupted, timely, secure or error-free; and
            </li>
            <li>
              (iii) the quality of the Content, or other material obtained by Users through the Site
              will meet Users’ expectations; and
            </li>
          </ol>
        </li>
        <li>
          any Content downloaded or otherwise obtained through the Site is accessed at Users’ own
          discretion and risk, and Users are solely responsible for any damage to their computer or
          loss of data that results from downloading Content.
        </li>
      </ol>

      <h4>11.2 Limitation of liability</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Subject to clause 11.3, Teach Starter does not provide any guarantee or warranty or make
          any representation of any kind, either express or implied, in relation to the Content or
          Site.
        </li>
        <li>
          Subject to clause 11.3 and to any claims available under consumer protection laws, Teach
          Starter and its officers, employees, contractors and agents are not liable for any loss or
          damage, including, but not limited to, direct or indirect losses however suffered or
          sustained in connection with:

          <ol style="list-style-type: lower-roman">
            <li>any inaccurate or incorrect information in the Content;</li>
            <li>Users’ use of the Content;</li>
            <li>
              (iii) any failure or delay including, but not limited to, the use or inability to use
              any of the Content or Site;
            </li>
            <li>
              (iv) any interference with or damage to Users’ computer systems which occurs in
              connection with use of the Content or Site;
            </li>
            <li>
              the cost of procurements of substitute goods and services resulting from any Content,
              goods or services purchased or obtained through the Site;
            </li>
            <li>any unauthorised access to or alteration of a User’s account information;</li>
            <li>
              (vii) for anything a User does to a third party as a result of using the Content or
              Site;
            </li>
            <li>for any inability to use any third party equipment or access to data;</li>
            <li>
              for loss or corruption of data, loss of business, loss of profits, loss of revenue and
              anticipated savings, business interruption of the like regardless of whether the loss
              is direct or indirectly caused by use of the Content or Site;
            </li>
            <li>
              for any indirect, incidental, punitive or special loss or damage whatsoever, in each
              case, arising out of the use or inability to use the Content or Site, even if Teach
              Starter has been advised of the possibility of such damages or if such damages are
              foreseeable;
            </li>
            <li>
              (xi) for any loss or damage to a User’s computer, technology, data or other material
              caused by a distributed denial of service or similar attack, any virus, trojan, worm,
              disabling feature, malware or other material which is malicious or technologically
              harmful due to the use of the Site or the Contents.
            </li>
          </ol>
        </li>
        <li>
          For claims that cannot be excluded or restricted under consumer protection laws, the
          liability of Teach Starter for such a claim will be limited to:

          <ol style="list-style-type: lower-alpha">
            <li>
              in the case of goods: repairing or replacing those goods or paying the cost of having
              those goods repaired or replaced; and
            </li>
            <li>
              if the breach relates to Content or Services: resupplying that or equivalent Content
              or Services, or paying the cost of having the Content or Services resupplied.
            </li>
          </ol>
        </li>
      </ol>

      <h3>11.3 Consumer Guarantees</h3>
      <h4>11.3</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          Notwithstanding the above, Teach Starter goods and services come with guarantees that
          cannot be excluded under the Australian Consumer Law. For major failures with the service,
          Users are entitled to cancel their service contract with Teach Starter and to a refund for
          the unused portion, or to compensation for its reduced value.
        </li>
        <li>
          Users are also entitled to choose a refund or replacement for major failures with goods.
          If a failure with the goods or a service does not amount to a major failure, Users are
          entitled to have the failure rectified in a reasonable time. If this is not done Users are
          entitled to a refund for the goods and to cancel the contract for the service and obtain a
          refund of any unused portion. Users are also entitled to be compensated for any other
          reasonably foreseeable loss or damage from a failure in the goods or service.
        </li>
      </ol>

      <p>11.4 Indemnity</p>
      <p>
        Users indemnify, hold harmless and will defend Teach Starter and its officers, employees,
        authorised representatives and agents against any claim or proceeding that is made,
        threatened or commenced, and any liability, loss, damage or expense (including reasonable
        legal costs on a full indemnity basis) that Teach Starter may incur or suffer as a direct or
        indirect result of:
      </p>
      <ol style="list-style-type: lower-alpha">
        <li>any breach of these Terms of Use;</li>
        <li>a User’s negligent acts or omissions;</li>
        <li>
          an actual or alleged breach by a User of any law, legislation, regulation, by-law or code
          of conduct caused by the Content or other data uploaded to or downloaded from the Site;
        </li>
        <li>
          use of the Content or Site, including any third party claims made in connection with, or
          arising out of, the User’s use of the Content and Site;
        </li>
        <li>
          any claims brought by or on behalf of a third party relating to any act or omission by the
          User, including breach of a third party’s copyright, trade mark or licence or use of a
          Third Party Product.
        </li>
      </ol>

      <h3>12. Notices</h3>
      <h4>12.1</h4>
      <ol style="list-style-type: lower-alpha">
        <li>
          For all correspondence in relation to these Terms of Use, please contact Teach Starter on:
          <ol style="list-style-type: lower-alpha">
            <li>
              email:
              <a href="mailto:support@teachstarter.com" title="Email Teach Starter support">
                support@teachstarter.com
              </a>
            </li>
          </ol>
        </li>
        <li>
          If required, Teach Starter will contact Users via the contact details the User has
          provided to Teach Starter. If Teach Starter has multiple contact details for a User, Teach
          Starter will contact the User using the most recent contact details on file.
        </li>
      </ol>

      <h3>13. Miscellaneous provisions</h3>
      <h4>13.1 Access to the Site</h4>
      <p>
        No representation or warranty is made that the Content and Site comply with the laws of any
        country outside of Australia. A User accessing the Site from outside Australia, does so at
        their own risk.
      </p>
      <h4>13.2 RSS Feeds</h4>
      <p>
        If a User utilises any features on the Site such as RSS Feed(s) (Feed) that may or may not
        (as the case may be) available from time to time, the format of the Feed must not be
        interfered with. The User must include the back link to the full article on the Site and
        show any included attestation.
      </p>
      <h4>13.3 Trade marks</h4>
      <p>
        Teach Starter may be the owner of several common law or registered trade marks which appear
        on the Site. Unauthorised use of these trade marks will infringe Teach Starter’s
        Intellectual Property rights and Teach Starter reserves its rights to take action against
        that User.
      </p>
      <h4>13.4 Amendments</h4>
      <p>
        Teach Starter may change the Terms of Use at its discretion by providing notice on the Site.
        The version of the Terms of Use that applies to Users will be available on the Site each
        time a User visits the Site.
      </p>
      <h4>13.5 Entire agreement</h4>
      <p>
        These Terms of Use (including the Subscriptions) and any warranties implied by law which are
        not capable of being excluded or modified and the Privacy Policy amount to the entire
        agreement with Users. Any contact with Teach Starter and its officers, employees,
        contractors, agents or authorised representatives that includes any statements,
        representations, warranties (if any) whether expressed or implied, including any collateral
        agreement or warranty, with reference to the subject matter or the intentions of either of
        the parties are merged and otherwise are excluded and cancelled by those contained in these
        Terms of Use.
      </p>
      <h4>13.6 Governing law and jurisdiction</h4>
      <p>
        Teach Starter’s websites and apps (excluding linked sites) is operated by Teach Starter from
        its offices in Australia. It can be accessed from Australia and from other countries around
        the world. As each of these places has laws that may differ from those of Australia, by
        accessing the Teach Starter website, the User agrees that these Terms of Use and the Users
        use of the Teach Starter website shall be governed in all respects by the internal
        substantive laws of Australia or if otherwise designated in a controversy, the laws of the
        United States, without regard to conflict of law’s provisions.
      </p>
      <p>
        Teach Starter makes no representation that materials on its website are appropriate or
        available for use in other locations, and accessing them from territories where their
        contents are illegal is prohibited. Users who choose to access this site from other
        locations do so on their own initiative and are responsible for compliance with local laws.
      </p>
      <p>
        Furthermore, any controversy or claim brought by Teach Starter against the User or by the
        User against Teach Starter or, its agents and/or employees arising out of or relating to
        these Terms of Use or the Teach Starter website shall be settled by binding arbitration in
        accordance with the commercial arbitration rules of the International Arbitration
        Association located in Australia or in Texas at a city of Teach Starter’s choosing in its
        discretion. Any such controversy or claim brought by User shall be arbitrated on an
        individual basis, and shall not be consolidated in any arbitration with any claim or
        controversy of any other party. The judgment on the arbitration award may be entered into
        any court having jurisdiction thereof.
      </p>
      <p>
        Alternatively, Teach Starter may in its sole and absolute discretion commence a law suit
        regarding such controversy or claim in any court located in Australia or in the United
        States in its absolute discretion to bring suit in any city where User is located or Teach
        Starter or its subsidiary is located.
      </p>
      <p>
        User hereby agrees to the jurisdiction and venue of such court with respect to such suit and
        waive any objections to such jurisdiction and/or venue of such court. User further agrees
        that the breach by it of the terms of this agreement will irreparably injure Teach Starter
        such that Teach Starter will be entitled to a preliminary injunction to prevent the
        continuance of such injury without the posting of bond. Further, should Teach Starter
        prevail in any law suit or arbitration with the User it shall be entitled to all of its
        attorney’s fees and costs.
      </p>
      <h4>13.7 Severability</h4>
      <p>
        Part or all of a provision of these Terms of Use that is illegal or unenforceable may be
        severed from these Terms of Use and the remaining parts of the provision or provisions of
        these Terms of Use continue in force.
      </p>
      <h4>13.8 Waiver</h4>
      <p>
        If Teach Starter does not act in relation to a particular breach by a User of these Terms of
        Use, this will not be treated as a waiver of Teach Starter’s right to act with respect to
        subsequent or similar breaches.
      </p>
    </div>
  </section>
</template>

<script>
/* Terms and Conditions */

export default {
  name: 'TermsAndConditions',
  data() {
    return {
      title: 'Teach Starter Terms and Conditions',
      description:
        'By utilizing the remainder of this website and related apps, you acknowledge that you have reviewed the Terms and Conditions and Privacy Policy and agree to them',
    };
  },
  head() {
    return {
      title: this.title,
      meta: [
        { hid: 'title', name: 'title', content: this.title },
        {
          hid: 'description',
          name: 'description',
          content: this.description,
        },
      ],
      link: this.$hrefLangsPage(this.$route),
    };
  },
};
</script>

<style scoped lang="scss">
ol {
  margin-top: $base-padding;
  margin-bottom: $base-padding;

  ol {
    margin-top: $base-padding;
  }
}
</style>
